import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MapPin, Store, Users, TrendingUp, Camera, X } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [patronPic, setPatronPic] = useState(null);
  const [ownerPic, setOwnerPic] = useState(null);
  const patronInputRef = useRef(null);
  const ownerInputRef = useRef(null);

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'patron') {
          setPatronPic(reader.result);
        } else {
          setOwnerPic(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePic = (e, type) => {
    e.stopPropagation();
    if (type === 'patron') {
      setPatronPic(null);
      if (patronInputRef.current) patronInputRef.current.value = '';
    } else {
      setOwnerPic(null);
      if (ownerInputRef.current) ownerInputRef.current.value = '';
    }
  };

  const handleNavigate = (path, pic) => {
    if (pic) {
      sessionStorage.setItem('pendingProfilePic', pic);
    }
    navigate(path);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'transparent' }}>
      <div className="max-w-6xl w-full">
        {/* Header */}
        <div className="text-center mb-10 fade-in">
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/barbuzz-logo.jpeg" 
              alt="Bar Buzz" 
              className="h-32 md:h-40 rounded-2xl shadow-2xl shadow-purple-500/30"
            />
          </div>
          <p className="text-xl text-slate-300">
            Find the hottest bars, tag your friends, and enjoy exclusive discounts
          </p>
        </div>

        {/* Login Options */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Patron Login */}
          <Card className="glass p-8 border-2 border-purple-500 hover:border-purple-400 transition-all group">
            <div className="text-center">
              {/* Photo Upload Circle */}
              <div 
                className="relative w-24 h-24 mx-auto mb-6 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  patronInputRef.current?.click();
                }}
              >
                <div className={`w-full h-full rounded-full border-4 ${patronPic ? 'border-purple-500' : 'border-dashed border-purple-500/50 hover:border-purple-400'} transition-all flex items-center justify-center overflow-hidden bg-purple-600/20 group-hover:scale-105`}>
                  {patronPic ? (
                    <img src={patronPic} alt="Your Photo" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <Camera className="w-8 h-8 text-purple-400 mx-auto" />
                      <span className="text-xs text-purple-400">Add Photo</span>
                    </div>
                  )}
                </div>
                {patronPic && (
                  <button
                    onClick={(e) => handleRemovePic(e, 'patron')}
                    className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                )}
                <input
                  ref={patronInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect(e, 'patron')}
                  className="hidden"
                />
              </div>

              <h2 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
                I'm a Patron
              </h2>
              <p className="text-slate-300 mb-6">
                Discover bars, check-in with friends, and get exclusive discounts
              </p>
              <ul className="text-left text-slate-300 space-y-2 mb-6">
                <li className="flex items-center gap-2">
                  <span className="text-purple-400">✓</span>
                  Find busy bars near you
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-purple-400">✓</span>
                  Tag friends in videos
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-purple-400">✓</span>
                  Get QR code discounts
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-purple-400">✓</span>
                  See girl-to-guy ratios & age groups
                </li>
              </ul>
              <Button 
                className="w-full bg-purple-600 hover:bg-purple-700 text-white text-lg py-6"
                onClick={() => handleNavigate('/auth', patronPic)}
              >
                Login as Patron
              </Button>
            </div>
          </Card>

          {/* Bar Owner Login */}
          <Card className="glass p-8 border-2 border-indigo-500 hover:border-indigo-400 transition-all group">
            <div className="text-center">
              {/* Photo Upload Circle */}
              <div 
                className="relative w-24 h-24 mx-auto mb-6 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  ownerInputRef.current?.click();
                }}
              >
                <div className={`w-full h-full rounded-full border-4 ${ownerPic ? 'border-indigo-500' : 'border-dashed border-indigo-500/50 hover:border-indigo-400'} transition-all flex items-center justify-center overflow-hidden bg-indigo-600/20 group-hover:scale-105`}>
                  {ownerPic ? (
                    <img src={ownerPic} alt="Your Photo" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <Camera className="w-8 h-8 text-indigo-400 mx-auto" />
                      <span className="text-xs text-indigo-400">Add Photo</span>
                    </div>
                  )}
                </div>
                {ownerPic && (
                  <button
                    onClick={(e) => handleRemovePic(e, 'owner')}
                    className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                )}
                <input
                  ref={ownerInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect(e, 'owner')}
                  className="hidden"
                />
              </div>

              <h2 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
                I'm a Bar Owner
              </h2>
              <p className="text-slate-300 mb-6">
                Manage your bar, post ads, and reach more customers
              </p>
              <ul className="text-left text-slate-300 space-y-2 mb-6">
                <li className="flex items-center gap-2">
                  <span className="text-indigo-400">✓</span>
                  Claim your bar listing
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-indigo-400">✓</span>
                  Update drink specials
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-indigo-400">✓</span>
                  Post promotional ads
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-indigo-400">✓</span>
                  View detailed analytics
                </li>
              </ul>
              <Button 
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-lg py-6"
                onClick={() => handleNavigate('/bar-owner/auth', ownerPic)}
              >
                Login as Bar Owner
              </Button>
            </div>
          </Card>
        </div>

        {/* Features */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          <Card className="glass p-6 border border-slate-700 text-center">
            <MapPin className="w-12 h-12 text-purple-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Live Location</h3>
            <p className="text-slate-300 text-sm">Find busy bars with real-time check-ins</p>
          </Card>
          <Card className="glass p-6 border border-slate-700 text-center">
            <Users className="w-12 h-12 text-purple-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Social Features</h3>
            <p className="text-slate-300 text-sm">Tag friends and see who's where</p>
          </Card>
          <Card className="glass p-6 border border-slate-700 text-center">
            <TrendingUp className="w-12 h-12 text-purple-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Live Stats</h3>
            <p className="text-slate-300 text-sm">Check ratios and demographics</p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Landing;
